import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Box,
  ButtonProps,
  Flex,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import { RiScreenshot2Line } from "react-icons/ri";
import { useIntl } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import {
  BuildingsTerrainIcon,
  CompassIcon,
  DrawIcon,
  ExitFullscreenIcon,
  FullscreenIcon,
  InfoIcon,
  InspectIcon,
  MapIcon,
  MessageIcon,
  MinusIcon,
  PlusIcon,
  RanaButton,
} from "@nens/ranalib";
import { HIDE_PRINT_CLASS } from "@nens/react/components";
import { useAnimationFrame } from "@nens/react/hooks";
import { objectEntries } from "@nens/shared-utils";

import { RanaMapExport } from "@/lib/rana-map-export";
import { ScreenRecordButton } from "@/lib/rana-screenrecord-button";

import { useRanaMapBackground } from "../utils/background";
interface MapButtonProps extends ButtonProps {
  roundT?: boolean;
  roundB?: boolean;
  roundR?: boolean;
  roundL?: boolean;
}

const MapButton = ({ roundT, roundB, roundR, roundL, ...rest }: MapButtonProps) => (
  <RanaButton
    className={HIDE_PRINT_CLASS}
    roundedTop={roundT ? "base" : undefined}
    roundedBottom={roundB ? "base" : undefined}
    roundedRight={roundR ? "base" : undefined}
    roundedLeft={roundL ? "base" : undefined}
    position="relative"
    rounded="none"
    display="flex"
    variant="tertiary"
    boxSize={"button"}
    borderWidth={0}
    p={0}
    justifyContent="center"
    {...rest}
  />
);

interface AddCommentProps extends MapButtonProps {
  setIsAddingComment: (isAddingComment: boolean) => void;
  isAddingComment: boolean;
  canComment: boolean;
}

const AddComment = ({
  setIsAddingComment,
  isAddingComment,
  canComment,
  ...rest
}: AddCommentProps) => {
  const intl = useIntl();
  return (
    <MapButton
      isActive={isAddingComment}
      title={
        canComment
          ? intl.formatMessage({
              id: "map.set_to_comment_mode",
              defaultMessage: "Set to comment mode",
            })
          : intl.formatMessage({
              id: "map.cannot_comment",
              defaultMessage:
                "Cannot add comments to the draft version of a publication (currently under construction)",
            })
      }
      isDisabled={!canComment}
      onClick={() => setIsAddingComment(true)}
      {...rest}
    >
      <Flex position="absolute" top="1/2" left="1/2" transform="translate(-50%, -50%)">
        <MessageIcon />
      </Flex>
    </MapButton>
  );
};

const BackgroundMap = ({ mapId, ...props }: MapButtonProps & { mapId: string }) => {
  const { backgroundMapKey, setBackgroundMapKey, backgrounds } = useRanaMapBackground(mapId);
  const intl = useIntl();
  return (
    <Menu>
      {/* Change background map */}
      <MapButton
        as={MenuButton}
        aria-label="Open menu"
        title={intl.formatMessage({
          id: "map.change_background_map",
          defaultMessage: "Change background map",
        })}
        {...props}
      >
        <MapIcon />
      </MapButton>
      <MenuList zIndex="fullscreenmap">
        <MenuOptionGroup defaultValue={backgroundMapKey} type="radio">
          {objectEntries(backgrounds).map(([key, { title, description, disabledTitle }]) => (
            <MenuItemOption
              value={key}
              key={key}
              isDisabled={!!disabledTitle}
              title={disabledTitle ?? description}
              onClick={() => setBackgroundMapKey(key)}
            >
              {title}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
};

const ScreenRecord = (props: MapButtonProps) => {
  return (
    <MapButton {...props}>
      <ScreenRecordButton borderWidth="0" as="div" />
    </MapButton>
  );
};

interface ThreeDProps extends MapButtonProps {
  setThreeD: () => void;
  isThreeD: boolean;
}

const ThreeD = ({ setThreeD, isThreeD, ...rest }: ThreeDProps) => {
  const intl = useIntl();
  return (
    <MapButton
      isActive={isThreeD}
      title={intl.formatMessage({
        id: "map.set_to_3d",
        defaultMessage:
          "Set to 3D (can also be done by holding right click or ctrl/cmd and dragging)",
      })}
      onClick={setThreeD}
      {...rest}
    >
      3D
    </MapButton>
  );
};

interface BuildingsAndTerrainProps extends MapButtonProps {
  setShow3DBuildings: (show3DBuildings: boolean) => void;
  show3DBuildings: boolean;
}

const BuildingsAndTerrain = ({
  setShow3DBuildings,
  show3DBuildings,
  ...rest
}: BuildingsAndTerrainProps) => {
  const intl = useIntl();
  return (
    <MapButton
      isActive={show3DBuildings}
      title={intl.formatMessage({
        id: "map.toggle_buildings_and_terrain",
        defaultMessage: "Toggle 3D buildings and terrain",
      })}
      onClick={() => setShow3DBuildings(!show3DBuildings)}
      {...rest}
    >
      <BuildingsTerrainIcon boxSize={5} />
    </MapButton>
  );
};

interface ZoomInProps extends MapButtonProps {
  zoomIn: () => void;
}

const ZoomIn = ({ zoomIn, ...rest }: ZoomInProps) => {
  const intl = useIntl();
  return (
    <MapButton
      title={intl.formatMessage({
        id: "map.zoom_in",
        defaultMessage: "Zoom in",
      })}
      onClick={zoomIn}
      {...rest}
    >
      <PlusIcon boxSize={4} />
    </MapButton>
  );
};

interface ZoomOutProps extends MapButtonProps {
  zoomOut: () => void;
}

const ZoomOut = ({ zoomOut, ...rest }: ZoomOutProps) => {
  const intl = useIntl();
  return (
    <MapButton
      title={intl.formatMessage({
        id: "map.zoom_out",
        defaultMessage: "Zoom out",
      })}
      onClick={zoomOut}
      {...rest}
    >
      <MinusIcon boxSize={4} />
    </MapButton>
  );
};

interface CompassProps extends MapButtonProps {
  mapId: string;
  toFlat: () => void;
}

const Compass = ({ mapId, toFlat, ...rest }: CompassProps) => {
  const intl = useIntl();
  const { [mapId]: map } = useMap();
  const [bearing, setBearing] = useState(0);

  useAnimationFrame(() => {
    setBearing(map?.getBearing() ?? 0);
  }, 100);

  return (
    <MapButton
      title={intl.formatMessage({
        id: "map.go_flat",
        defaultMessage: "Go to flat",
      })}
      onClick={toFlat}
      {...rest}
    >
      <Box transform={`rotate(${-bearing}deg)`}>
        <CompassIcon />
      </Box>
    </MapButton>
  );
};

const Inspect = (props: MapButtonProps) => {
  const intl = useIntl();
  return (
    <MapButton
      title={intl.formatMessage({
        id: "map.inspect",
        defaultMessage: "Inspect",
      })}
      {...props}
    >
      <InspectIcon />
    </MapButton>
  );
};

const Draw = (props: MapButtonProps) => {
  const intl = useIntl();
  return (
    <MapButton
      title={intl.formatMessage({
        id: "map.draw",
        defaultMessage: "Draw",
      })}
      {...props}
    >
      <DrawIcon />
    </MapButton>
  );
};

interface FullscreenProps extends MapButtonProps {
  fullscreen: boolean;
  setFullscreen: (isFullscreen: boolean) => void;
}

const Fullscreen = ({ fullscreen, setFullscreen, ...rest }: FullscreenProps) => {
  const intl = useIntl();

  return (
    <MapButton
      isActive={fullscreen}
      title={intl.formatMessage(
        fullscreen
          ? { id: "map.exit_fullscreen", defaultMessage: "Exit fullscreen" }
          : { id: "map.enter_fullscreen", defaultMessage: "Enter fullscreen" }
      )}
      onClick={() => setFullscreen(!fullscreen)}
      {...rest}
    >
      {fullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
    </MapButton>
  );
};

const Attribution = (props: MapButtonProps) => {
  return (
    <Menu>
      {/* Change background map */}
      <MapButton as={MenuButton} aria-label="Open menu" {...props}>
        <InfoIcon />
      </MapButton>

      <Portal>
        <MenuList zIndex="fullscreenmap" overflow="hidden" color="brand.bg">
          {[
            { url: "https://maplibre.org/", label: "Maplibre" },
            { url: "https://www.maptiler.com/copyright/", label: "© MapTiler" },
            {
              url: "https://www.openstreetmap.org/copyright",
              label: "© OpenStreetMap",
            },
            { url: "https://api.pdok.nl/kadaster/brt-achtergrondkaart/ogc/v1", label: "PDOK" },
            { url: "https://docs.3dbag.nl/en/copyright/", label: "© 3DBAG by tudelft3d and 3DGI" },
          ].map(({ url, label }) => (
            <MenuItem
              as={Link}
              isExternal
              icon={<ExternalLinkIcon />}
              _hover={{ textDecor: "underline" }}
              key={label}
              href={url}
            >
              {label}
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
};

interface MapExportProps extends MapButtonProps {
  mapId: string;
  fullscreen: boolean;
}

const MapExport = ({ mapId, fullscreen, ...rest }: MapExportProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const intl = useIntl();
  return (
    <>
      <MapButton
        title={intl.formatMessage({
          id: "map.export_map",
          defaultMessage: "Export map",
        })}
        onClick={() => {
          onOpen();
        }}
        {...rest}
      >
        <RiScreenshot2Line size={24} />
      </MapButton>
      {isOpen && <RanaMapExport initialFullscreen={fullscreen} mapId={mapId} onClose={onClose} />}
    </>
  );
};

export const RanaMapButtons = {
  MapButton,
  AddComment,
  BackgroundMap,
  ScreenRecord,
  ThreeD,
  ZoomIn,
  ZoomOut,
  Compass,
  BuildingsAndTerrain,
  Inspect,
  Draw,
  Attribution,
  Fullscreen,
  MapExport,
};
